import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Check, Loader2, X, Archive } from "lucide-react";
import { listAllPartnerEvents, reviewPartnerEvent } from "@/lib/partner-events.functions";

export const Route = createFileRoute("/_authenticated/admin/partner-events")({
  head: () => ({
    meta: [
      { title: "Partner Events — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminPartnerEvents,
});

type Row = {
  id: string;
  title: string;
  description: string | null;
  starts_at: string;
  ends_at: string | null;
  location: string | null;
  ticket_url: string | null;
  image_url: string | null;
  status: string;
  admin_note: string | null;
  client_id: string;
  crm_clients?: { id: string; name: string | null; company_name: string | null } | null;
};

const STATUSES = ["submitted", "approved", "rejected", "draft", "archived"] as const;
const STATUS_CLASS: Record<string, string> = {
  draft: "bg-slate-100 text-slate-700",
  submitted: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
  archived: "bg-slate-100 text-slate-500",
};

function AdminPartnerEvents() {
  const [filter, setFilter] = useState<string>("submitted");
  const qc = useQueryClient();
  const listFn = useServerFn(listAllPartnerEvents);
  const reviewFn = useServerFn(reviewPartnerEvent);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "partner-events", filter],
    queryFn: () => listFn({ data: filter ? { status: filter } : {} }) as Promise<Row[]>,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "partner-events"] });

  const review = useMutation({
    mutationFn: (v: { id: string; action: "approve" | "reject" | "archive"; admin_note?: string }) =>
      reviewFn({ data: v }),
    onSuccess: (_r, v) => {
      toast.success(`Event ${v.action}d`);
      invalidate();
    },
    onError: (e: unknown) => toast.error((e as Error)?.message || "Failed"),
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900">Partner Events</h1>
          <p className="text-sm text-slate-500 mt-1">Review venue-submitted events. Approved events appear on the public site.</p>
        </header>

        <div className="flex flex-wrap gap-2 mb-5">
          <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-xl text-sm ${filter === "" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>All</button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-xl text-sm capitalize ${filter === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>{s}</button>
          ))}
        </div>

        {isLoading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {!isLoading && rows.length === 0 && <div className="text-sm text-slate-500">No events.</div>}

        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex gap-4">
              {r.image_url && <img src={r.image_url} alt="" className="w-28 h-28 rounded-xl object-cover border border-slate-200 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900">{r.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {r.crm_clients?.company_name || r.crm_clients?.name || "Partner"}
                      {" · "}
                      {new Date(r.starts_at).toLocaleString()}
                      {r.ends_at && ` — ${new Date(r.ends_at).toLocaleString()}`}
                      {r.location && ` · ${r.location}`}
                    </div>
                    {r.description && <div className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{r.description}</div>}
                    {r.ticket_url && <a href={r.ticket_url} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-slate-800 mt-2 block">Ticket link ↗</a>}
                    {r.admin_note && (
                      <div className="text-xs text-slate-600 mt-2">Note: {r.admin_note}</div>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_CLASS[r.status] || "bg-slate-100 text-slate-700"}`}>{r.status}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {r.status !== "approved" && (
                    <button onClick={() => review.mutate({ id: r.id, action: "approve" })} className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 text-white text-xs font-medium px-3 py-1.5 hover:bg-emerald-700">
                      <Check className="h-3 w-3" /> Approve
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button onClick={() => {
                      const note = prompt("Reason for rejection (visible to partner):") || undefined;
                      review.mutate({ id: r.id, action: "reject", admin_note: note });
                    }} className="inline-flex items-center gap-1 rounded-xl border border-slate-300 bg-white text-xs font-medium px-3 py-1.5 text-slate-700 hover:bg-slate-100">
                      <X className="h-3 w-3" /> Reject
                    </button>
                  )}
                  <button onClick={() => review.mutate({ id: r.id, action: "archive" })} className="inline-flex items-center gap-1 rounded-xl border border-slate-300 bg-white text-xs font-medium px-3 py-1.5 text-slate-600 hover:bg-slate-100">
                    <Archive className="h-3 w-3" /> Archive
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
